import {
  ArrowPathIcon,
  ChevronUpDownIcon,
  InformationCircleIcon,
  PaperAirplaneIcon,
  PlusIcon
} from '@heroicons/react/20/solid';
import { ChatBubbleLeftRightIcon } from '@heroicons/react/24/outline';
import { animated, useSpring } from '@react-spring/web';
import { useAppDispatch, useAppSelector } from 'app/hooks';
import { RootState } from 'app/store';
import clsx from 'clsx';
import Textarea from 'components/inputs/textarea';
import {
  Popover,
  PopoverClose,
  PopoverContent,
  PopoverTrigger
} from 'components/layout/popover';
import Tooltip from 'components/layout/tooltip';
import {
  createChatbotConversation,
  fetchChatbotCategories,
  fetchChatbotConversations,
  fetchChatbotMessages,
  PendingDesignRuleFix,
  sendChatbotMessage,
  setActiveConversationUniqueId,
  setMessages,
  setPendingEvaluation,
  setPendingFix,
  setPendingMultiFix
} from 'features/chatbot';
import { toggleChatPanel } from 'features/ui';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import Markdown from 'react-markdown';
import { useSelector } from 'react-redux';
import remarkGfm from 'remark-gfm';

const stripEmoji = (text: string): string =>
  text
    .replace(/\?\?/g, '')
    .replaceAll(
      /\p{Extended_Pictographic}(?:\u{FE0F}|\u{200D}\p{Extended_Pictographic})*/gu,
      ''
    )
    .replace(/ {2,}/g, ' ')
    .trim();

const TAB_TO_CATEGORY_CODE: Record<string, string> = {
  structure: 'layout_sizing',
  configurations: 'system_design'
};

export default function ChatBot() {
  const dispatch = useAppDispatch();
  const { showChatPanel, activeTab } = useAppSelector((state) => state.ui);
  const [prompt, setPrompt] = useState('');
  const [elapsedTime, setElapsedTime] = useState(0);
  const [activeFix, setActiveFix] = useState<PendingDesignRuleFix | null>(null);
  const [activeMultiFix, setActiveMultiFix] = useState<
    PendingDesignRuleFix[] | null
  >(null);
  const [selectedRecs, setSelectedRecs] = useState<Set<string>>(new Set());
  const [categoryOverride, setCategoryOverride] = useState<string | null>(null);

  const lastMessageRef = useRef<HTMLDivElement>(null);
  const skipMessageFetchRef = useRef(false);

  const structureUniqueId = useSelector(
    (state: RootState) => state.structure.structureUniqueId
  );
  const { projectDetails, instanceUniqueId } = useAppSelector(
    (state) => state.project
  );
  const { configurations, activeConfig } = useAppSelector(
    (state) => state.configurations
  );
  const selectedConfigurationUniqueId =
    configurations[activeConfig]?.uniqueId ?? undefined;
  const { activeRequest, pollingStatus, workflowHistory, requestQueue } =
    useAppSelector((state) => state.ai);
  const {
    categories,
    conversations,
    activeConversationUniqueId,
    messages,
    loadingMessages,
    sendingMessage,
    processingStatus,
    pendingFix,
    pendingMultiFix,
    pendingEvaluation
  } = useAppSelector((state) => state.chatbot);

  const projectId = projectDetails?.projectId;

  const spring = useSpring({
    width: showChatPanel ? '400px' : '0px',
    config: { tension: 300, friction: 30 }
  });

  const currentCategoryCode =
    categoryOverride ?? TAB_TO_CATEGORY_CODE[activeTab] ?? 'general';

  const currentCategory = useMemo(
    () => categories.find((c) => c.code === currentCategoryCode) ?? null,
    [categories, currentCategoryCode]
  );

  const filteredConversations = useMemo(
    () =>
      currentCategory
        ? conversations.filter((c) => c.categoryId === currentCategory.id)
        : conversations,
    [conversations, currentCategory]
  );

  const activeConversationTitle =
    filteredConversations.find((c) => c.uniqueId === activeConversationUniqueId)
      ?.title || 'New conversation';

  const scrollToBottom = useCallback(() => {
    lastMessageRef.current?.scrollIntoView();
  }, []);

  useEffect(() => {
    dispatch(fetchChatbotCategories());
  }, [dispatch]);

  useEffect(() => {
    setCategoryOverride(null);
  }, [activeTab]);

  useEffect(() => {
    if (structureUniqueId && categories.length > 0) {
      dispatch(
        fetchChatbotConversations({
          structureUniqueId,
          categoryId: currentCategory?.id
        })
      );
    }
  }, [dispatch, structureUniqueId, categories, currentCategory?.id]);

  useEffect(() => {
    if (activeConversationUniqueId) {
      if (skipMessageFetchRef.current) {
        skipMessageFetchRef.current = false;
        return;
      }
      dispatch(fetchChatbotMessages(activeConversationUniqueId));
    }
  }, [dispatch, activeConversationUniqueId]);

  useEffect(() => {
    scrollToBottom();
  }, [messages, scrollToBottom]);

  useEffect(() => {
    if (showChatPanel) {
      setTimeout(scrollToBottom, 50);
    }
  }, [showChatPanel, scrollToBottom]);

  useEffect(() => {
    if (pendingFix === null) return;
    setActiveFix(pendingFix);
    setActiveMultiFix(null);
    setSelectedRecs(new Set());
    dispatch(setPendingFix(null));
    dispatch(setActiveConversationUniqueId(null));
    dispatch(setMessages([]));
  }, [pendingFix, dispatch]);

  useEffect(() => {
    if (pendingMultiFix === null) return;
    setActiveMultiFix(pendingMultiFix);
    setActiveFix(null);
    setSelectedRecs(new Set());
    dispatch(setPendingMultiFix(null));
    dispatch(setActiveConversationUniqueId(null));
    dispatch(setMessages([]));
  }, [pendingMultiFix, dispatch]);

  useEffect(() => {
    if (pendingEvaluation === null) return;
    const evaluation = pendingEvaluation;
    dispatch(setPendingEvaluation(null));
    dispatch(setActiveConversationUniqueId(null));
    dispatch(setMessages([]));
    setActiveFix(null);
    setActiveMultiFix(null);
    setSelectedRecs(new Set());

    (async () => {
      if (!structureUniqueId || !projectId || !currentCategory) return;

      skipMessageFetchRef.current = true;
      const result = await dispatch(
        createChatbotConversation({
          structureUniqueId,
          categoryId: currentCategory.id
        })
      ).unwrap();

      const issuesSummary =
        evaluation.issues.length > 0
          ? `\n\nCurrent design rule validation results:\n${evaluation.issues
              .map(
                (i) => `- [${i.severity.toUpperCase()}] ${i.name}: ${i.message}`
              )
              .join('\n')}`
          : '\n\nThere are currently no design rule validation issues.';

      const message = `Please evaluate my current configuration and provide: 1) An overall score or rating for the configuration quality, 2) A summary of what is working well, 3) Specific recommendations for improvements that could be made.${issuesSummary}`;

      dispatch(
        sendChatbotMessage({
          conversationUniqueId: result.uniqueId,
          message,
          displayMessage: 'Evaluate my configuration',
          structureUniqueId,
          projectId,
          instanceUniqueId: instanceUniqueId ?? undefined,
          selectedConfigurationUniqueId
        })
      );
    })();
  }, [
    pendingEvaluation,
    dispatch,
    structureUniqueId,
    projectId,
    currentCategory,
    instanceUniqueId,
    selectedConfigurationUniqueId
  ]);

  useEffect(() => {
    if (pollingStatus === 'polling' && activeRequest) {
      const updateElapsed = () => {
        setElapsedTime(Date.now() - activeRequest.startTime);
      };
      updateElapsed();
      const interval = setInterval(updateElapsed, 1000);
      return () => clearInterval(interval);
    }
    setElapsedTime(0);
    return undefined;
  }, [pollingStatus, activeRequest]);

  const handleNewConversation = () => {
    setActiveFix(null);
    setActiveMultiFix(null);
    setSelectedRecs(new Set());
    dispatch(setActiveConversationUniqueId(null));
    dispatch(setMessages([]));
  };

  const handleConversationChange = (uniqueId: string) => {
    setActiveFix(null);
    setActiveMultiFix(null);
    setSelectedRecs(new Set());
    dispatch(setActiveConversationUniqueId(uniqueId));
  };

  const handleSend = async () => {
    if (
      sendingMessage ||
      processingStatus === 'processing' ||
      prompt.trim() === '' ||
      !structureUniqueId ||
      !projectId
    )
      return;

    let conversationUniqueId = activeConversationUniqueId;

    if (!conversationUniqueId) {
      if (!currentCategory) return;
      skipMessageFetchRef.current = true;
      const result = await dispatch(
        createChatbotConversation({
          structureUniqueId,
          categoryId: currentCategory.id
        })
      ).unwrap();
      conversationUniqueId = result.uniqueId;
    }

    dispatch(
      sendChatbotMessage({
        conversationUniqueId,
        message: prompt,
        structureUniqueId,
        projectId,
        instanceUniqueId: instanceUniqueId ?? undefined,
        selectedConfigurationUniqueId
      })
    );
    setPrompt('');
  };

  const handleRecommendationSelect = async (
    fix: PendingDesignRuleFix,
    rec: { recommendation: string; aiInstructions: string }
  ) => {
    if (!structureUniqueId || !projectId) return;

    setActiveFix(null);

    const message = `${fix.failureContext} ${rec.aiInstructions}`.trim();
    const displayMessage =
      `${fix.displayFailureContext} ${rec.recommendation}`.trim();

    let conversationUniqueId = activeConversationUniqueId;
    if (!conversationUniqueId) {
      if (!currentCategory) return;
      skipMessageFetchRef.current = true;
      const result = await dispatch(
        createChatbotConversation({
          structureUniqueId,
          categoryId: currentCategory.id
        })
      ).unwrap();
      conversationUniqueId = result.uniqueId;
    }

    dispatch(
      sendChatbotMessage({
        conversationUniqueId,
        message,
        displayMessage,
        structureUniqueId,
        projectId,
        instanceUniqueId: instanceUniqueId ?? undefined,
        selectedConfigurationUniqueId
      })
    );
  };

  const selectRec = (fixIdx: number, key: string) => {
    setSelectedRecs((prev) => {
      const next = new Set(prev);
      const prefix = `${fixIdx}-`;
      prev.forEach((k) => {
        if (k.startsWith(prefix)) next.delete(k);
      });
      next.add(key);
      return next;
    });
  };

  const handleMultiFixSend = async () => {
    if (!activeMultiFix || selectedRecs.size === 0) return;
    if (!structureUniqueId || !projectId) return;

    const parts: { message: string; display: string }[] = [];
    activeMultiFix.forEach((fix, fixIdx) => {
      fix.recommendations.forEach((rec, recIdx) => {
        if (!selectedRecs.has(`${fixIdx}-${recIdx}`)) return;
        parts.push({
          message: `${fix.failureContext} ${rec.aiInstructions}`.trim(),
          display: `${fix.displayFailureContext} ${rec.recommendation}`.trim()
        });
      });
    });

    if (parts.length === 0) return;

    const message = `Please discuss options for achieving all of the following recommendations:\n${parts
      .map((p) => p.message)
      .join('\n')}`;
    const displayMessage = `Please discuss options for achieving all of the following recommendations:\n${parts
      .map((p) => `- ${p.display}`)
      .join('\n')}`;

    setActiveMultiFix(null);
    setSelectedRecs(new Set());

    let conversationUniqueId = activeConversationUniqueId;
    if (!conversationUniqueId) {
      if (!currentCategory) return;
      skipMessageFetchRef.current = true;
      const result = await dispatch(
        createChatbotConversation({
          structureUniqueId,
          categoryId: currentCategory.id
        })
      ).unwrap();
      conversationUniqueId = result.uniqueId;
    }

    dispatch(
      sendChatbotMessage({
        conversationUniqueId,
        message,
        displayMessage,
        structureUniqueId,
        projectId,
        instanceUniqueId: instanceUniqueId ?? undefined,
        selectedConfigurationUniqueId
      })
    );
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const formatDuration = (durationMs: number) => {
    const totalSeconds = Math.floor(durationMs / 1000);
    if (totalSeconds < 60) return `${totalSeconds}s`;
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = totalSeconds % 60;
    if (minutes < 60) {
      return seconds > 0 ? `${minutes}m ${seconds}s` : `${minutes}m`;
    }
    const hours = Math.floor(minutes / 60);
    const remainingMinutes = minutes % 60;
    return remainingMinutes > 0
      ? `${hours}h ${remainingMinutes}m`
      : `${hours}h`;
  };

  const getStatusColor = (status: 'completed' | 'failed' | 'timeout') => {
    switch (status) {
      case 'completed':
        return 'text-green-600';
      case 'failed':
        return 'text-red-600';
      case 'timeout':
        return 'text-yellow-600';
      default:
        return 'text-gray-600';
    }
  };

  return (
    <div className='relative h-full'>
      <button
        type='button'
        className='absolute bottom-5 right-full z-20 flex h-16 w-16 cursor-pointer items-center justify-center rounded-l-full bg-white shadow-lg hover:bg-gray-50'
        onClick={() => dispatch(toggleChatPanel())}
      >
        <ChatBubbleLeftRightIcon className='h-8 w-8 text-gray-700' />
      </button>
      <animated.div
        className='relative h-full overflow-hidden border-l border-gray-200 bg-white'
        style={spring}
      >
        <div className='absolute flex h-full w-[400px] flex-col gap-3 overflow-hidden p-5'>
          <div className='flex items-center justify-between'>
            <div className='text-xl font-semibold text-gray-700'>
              AI Assistant
            </div>
            <Popover placement='bottom-end'>
              <PopoverTrigger asChild>
                <button
                  type='button'
                  className='flex items-center gap-1 text-xs text-gray-400 hover:text-gray-600'
                >
                  <span>{currentCategory?.label ?? 'General'}</span>
                  <ChevronUpDownIcon className='h-3 w-3' />
                </button>
              </PopoverTrigger>
              <PopoverContent className='z-50 max-h-60 w-56 overflow-auto rounded-md border border-gray-200 bg-white py-1 shadow-lg'>
                {categories.map((cat) => (
                  <PopoverClose
                    key={cat.id}
                    className={clsx(
                      'w-full cursor-pointer px-3 py-1.5 text-left text-sm hover:bg-gray-100',
                      cat.code === currentCategoryCode
                        ? 'bg-blue-50 font-medium text-blue-700'
                        : 'text-gray-700'
                    )}
                    onClick={() => {
                      setCategoryOverride(cat.code);
                      dispatch(setActiveConversationUniqueId(null));
                      dispatch(setMessages([]));
                    }}
                  >
                    {cat.label}
                  </PopoverClose>
                ))}
              </PopoverContent>
            </Popover>
          </div>

          <div className='flex items-center gap-2'>
            <Popover placement='bottom-start'>
              <PopoverTrigger asChild>
                <button
                  type='button'
                  className='relative flex min-w-0 flex-1 items-center justify-between rounded-md border border-gray-300 bg-white px-2 py-1.5 text-sm text-gray-700 focus:border-blue-500 focus:outline-none'
                >
                  <span className='truncate'>{activeConversationTitle}</span>
                  <ChevronUpDownIcon className='ml-1 h-4 w-4 flex-shrink-0 text-gray-400' />
                  <Tooltip text={activeConversationTitle} />
                </button>
              </PopoverTrigger>
              <PopoverContent className='z-50 max-h-60 w-[340px] overflow-auto rounded-md border border-gray-200 bg-white py-1 shadow-lg'>
                {filteredConversations.length === 0 && (
                  <div className='px-3 py-2 text-sm text-gray-400'>
                    No conversations
                  </div>
                )}
                {filteredConversations.map((conv) => (
                  <PopoverClose
                    key={conv.uniqueId}
                    className={clsx(
                      'w-full cursor-pointer px-3 py-1.5 text-left text-sm hover:bg-gray-100',
                      conv.uniqueId === activeConversationUniqueId
                        ? 'bg-blue-50 font-medium text-blue-700'
                        : 'text-gray-700'
                    )}
                    onClick={() => handleConversationChange(conv.uniqueId)}
                  >
                    {conv.title || 'New conversation'}
                  </PopoverClose>
                ))}
              </PopoverContent>
            </Popover>
            <button
              type='button'
              onClick={handleNewConversation}
              className='flex items-center gap-1 rounded-md bg-blue-500 px-2 py-1.5 text-sm text-white hover:bg-blue-600'
              title='New conversation'
            >
              <PlusIcon className='h-4 w-4' />
            </button>
          </div>

          <hr className='border-gray-200' />

          {(pollingStatus === 'polling' ||
            requestQueue.length > 0 ||
            workflowHistory.length > 0) && (
            <div className='rounded-md bg-gray-50 p-2 text-xs'>
              {pollingStatus === 'polling' && activeRequest && (
                <div className='mb-2 flex items-center gap-2 text-blue-600'>
                  <div className='h-2 w-2 animate-pulse rounded-full bg-blue-600' />
                  <span>
                    In Progress: {activeRequest.title} - Started{' '}
                    {formatDuration(elapsedTime)} ago
                  </span>
                </div>
              )}
              {requestQueue.length > 0 && (
                <div className='mb-2'>
                  <div className='text-gray-500'>Next Up:</div>
                  <div className='mt-1 space-y-1'>
                    {requestQueue.map((item) => (
                      <div
                        key={item.id}
                        className='flex items-center gap-1 text-gray-600'
                      >
                        <span>{item.title}</span>
                        <span className='relative'>
                          <InformationCircleIcon className='h-3.5 w-3.5 cursor-help text-gray-400' />
                          <Tooltip text={item.message} />
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              {workflowHistory.length > 0 && (
                <div className='mt-1'>
                  <div className='text-gray-500'>Recent:</div>
                  <div className='mt-1 space-y-1'>
                    {workflowHistory.slice(0, 5).map((entry) => (
                      <div
                        key={`${entry.requestId}-${entry.duration}`}
                        className='flex items-center justify-between'
                      >
                        <span className='flex items-center gap-1 text-gray-600'>
                          {entry.title}
                          <span className='relative'>
                            <InformationCircleIcon className='h-3.5 w-3.5 cursor-help text-gray-400' />
                            <Tooltip text={entry.message} />
                          </span>
                        </span>
                        <span className={getStatusColor(entry.status)}>
                          {entry.status} ({formatDuration(entry.duration)})
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          <div className='flex w-full grow flex-col gap-3 overflow-auto'>
            {!activeConversationUniqueId && !activeFix && !activeMultiFix && (
              <div className='flex flex-1 items-center justify-center text-sm text-gray-400'>
                Type a message to start
              </div>
            )}
            {activeConversationUniqueId && loadingMessages && (
              <div className='flex flex-1 items-center justify-center text-sm text-gray-400'>
                Loading messages...
              </div>
            )}
            {activeConversationUniqueId &&
              !loadingMessages &&
              messages
                .filter((msg) => msg.role !== 'system')
                .map((msg) => {
                  if (msg.role === 'user') {
                    const userMeta = msg.metadata
                      ? JSON.parse(msg.metadata)
                      : null;
                    const displayText = userMeta?.displayMessage ?? msg.content;
                    return (
                      <div key={msg.id} className='ml-16 flex justify-end'>
                        <span className='inline-block whitespace-pre-wrap rounded-t-xl rounded-bl-xl bg-blue-500 px-3 py-2 text-white'>
                          {displayText}
                        </span>
                      </div>
                    );
                  }

                  if (msg.role === 'tool') {
                    const meta = msg.metadata ? JSON.parse(msg.metadata) : null;
                    const tools = meta?.tools ?? [];
                    return (
                      <div key={msg.id} className='mr-16 flex justify-start'>
                        <div className='relative rounded-lg border border-gray-200 bg-gray-50 px-3 py-2 text-xs text-gray-500'>
                          {tools.map(
                            (
                              t: {
                                toolName: string;
                                success: boolean;
                                result?: string;
                                error?: string;
                              },
                              i: number
                            ) => (
                              // eslint-disable-next-line react/no-array-index-key
                              <div key={i} className='flex items-center gap-1'>
                                <span>{t.toolName}</span>
                                <span
                                  className={
                                    t.success
                                      ? 'text-green-600'
                                      : 'text-red-600'
                                  }
                                >
                                  {t.success ? 'OK' : 'ERR'}
                                </span>
                              </div>
                            )
                          )}
                          <Tooltip>
                            <div className='max-h-64 space-y-2 overflow-auto'>
                              {tools.map(
                                (
                                  t: {
                                    toolName: string;
                                    success: boolean;
                                    result?: string;
                                    error?: string;
                                  },
                                  i: number
                                ) => (
                                  // eslint-disable-next-line react/no-array-index-key
                                  <div key={i}>
                                    <div className='font-semibold'>
                                      {t.toolName}
                                    </div>
                                    <div className='whitespace-pre-wrap'>
                                      {t.success
                                        ? t.result ?? 'OK'
                                        : t.error ?? 'Failed'}
                                    </div>
                                  </div>
                                )
                              )}
                            </div>
                          </Tooltip>
                        </div>
                      </div>
                    );
                  }

                  const meta = msg.metadata ? JSON.parse(msg.metadata) : null;
                  if (meta?.type === 'sub_agent_handoff') {
                    return (
                      <div key={msg.id} className='mr-16 flex justify-start'>
                        <div className='flex items-center gap-2 rounded-lg border border-indigo-200 bg-indigo-50 px-3 py-2 text-xs text-indigo-700'>
                          <ArrowPathIcon className='h-4 w-4 flex-shrink-0' />
                          <span>
                            Delegating to <strong>{meta.subAgentName}</strong>
                            {`: ${meta.task}`}
                          </span>
                        </div>
                      </div>
                    );
                  }

                  return (
                    <div key={msg.id} className='mr-16 flex justify-start'>
                      <div className='prose prose-sm max-w-none rounded-t-xl rounded-br-xl bg-gray-100 px-3 py-2'>
                        <Markdown remarkPlugins={[remarkGfm]}>
                          {stripEmoji(msg.content)}
                        </Markdown>
                      </div>
                    </div>
                  );
                })}
            {activeFix && (
              <div className='mr-8 flex flex-col gap-2'>
                <div className='rounded-t-xl rounded-br-xl bg-gray-100 px-3 py-2'>
                  <div className='text-sm font-medium text-gray-800'>
                    How would you like to fix:{' '}
                    <strong>{activeFix.issueName}</strong>?
                  </div>
                  <div className='mt-1 text-xs text-gray-500'>
                    {activeFix.issueMessage}
                  </div>
                </div>
                <div className='flex flex-col gap-1.5'>
                  {activeFix.recommendations.map((rec) => (
                    <button
                      key={rec.recommendation}
                      type='button'
                      onClick={() => handleRecommendationSelect(activeFix, rec)}
                      className='rounded-lg border border-blue-200 bg-blue-50 px-3 py-2 text-left text-sm text-blue-700 hover:bg-blue-100'
                    >
                      {rec.recommendation}
                    </button>
                  ))}
                </div>
              </div>
            )}
            {activeMultiFix && (
              <div className='mr-8 flex flex-col gap-3'>
                <div className='rounded-t-xl rounded-br-xl bg-gray-100 px-3 py-2'>
                  <div className='text-sm font-medium text-gray-800'>
                    Select recommendations to discuss with AI
                  </div>
                  <div className='mt-1 text-xs text-gray-500'>
                    {activeMultiFix.length} issues selected — choose which
                    recommendations to include.
                  </div>
                </div>
                <div className='flex flex-col gap-3'>
                  {activeMultiFix.map((fix, fixIdx) => (
                    <div
                      key={`${fix.issueName}-${fix.failureContext}`}
                      className='flex flex-col gap-1.5'
                    >
                      <div className='text-xs font-semibold text-gray-700'>
                        {fix.issueName}
                        {fix.displayFailureContext && (
                          <span className='ml-1 font-normal text-gray-500'>
                            {fix.displayFailureContext}
                          </span>
                        )}
                      </div>
                      {fix.recommendations.map((rec, recIdx) => {
                        const recKey = `${fixIdx}-${recIdx}`;
                        return (
                          <label
                            key={recKey}
                            className={clsx(
                              'flex cursor-pointer items-start gap-2 rounded-lg border px-3 py-2 text-left text-sm',
                              selectedRecs.has(recKey)
                                ? 'border-blue-300 bg-blue-50 text-blue-700'
                                : 'border-gray-200 bg-white text-gray-700 hover:bg-gray-50'
                            )}
                          >
                            <input
                              type='radio'
                              name={`fix-${fixIdx}`}
                              checked={selectedRecs.has(recKey)}
                              onChange={() => selectRec(fixIdx, recKey)}
                              className='mt-0.5 h-4 w-4 shrink-0 border-gray-300 text-blue-600 focus:ring-blue-500'
                            />
                            <span>{rec.recommendation}</span>
                          </label>
                        );
                      })}
                    </div>
                  ))}
                </div>
                <button
                  type='button'
                  onClick={handleMultiFixSend}
                  disabled={selectedRecs.size === 0}
                  className='flex items-center justify-center gap-1.5 rounded-lg bg-blue-500 px-3 py-2 text-sm font-medium text-white hover:bg-blue-600 disabled:cursor-not-allowed disabled:opacity-40'
                >
                  Send selected ({selectedRecs.size})
                </button>
              </div>
            )}
            <div ref={lastMessageRef} />
          </div>

          {processingStatus === 'processing' && (
            <div className='flex items-center gap-2 text-sm text-gray-500'>
              <div className='h-2 w-2 animate-pulse rounded-full bg-blue-500' />
              Assistant is working...
            </div>
          )}

          <div className='flex gap-2'>
            <Textarea
              placeholder='How can I help?'
              onInput={(value) => setPrompt(value)}
              value={prompt}
              onKeyDown={handleKeyPress}
              autoFocus={showChatPanel}
              rows={3}
              controlled
            />
            <button
              type='button'
              onClick={handleSend}
              disabled={
                sendingMessage ||
                processingStatus === 'processing' ||
                prompt.trim() === ''
              }
              className='flex h-auto items-center justify-center rounded-md bg-blue-500 px-3 text-white hover:bg-blue-600 disabled:cursor-not-allowed disabled:opacity-50'
            >
              <PaperAirplaneIcon className='h-5 w-5' />
            </button>
          </div>
        </div>
      </animated.div>
    </div>
  );
}
